import { createSlice } from '@reduxjs/toolkit';
import {
  ICommitment,
  IInputTable,
  IOutputTable,
  IPages,
  IPtpDetail,
  IPtpDetailsFilters,
  IReasons,
  ISenseiPageState,
  filtersNames
} from '../types';
import { AGENT_TYPES } from '../components/OverallMetricsV2/constants';

const initialState: ISenseiPageState = {
  dailyPlanning: {
    filters: {
      loading: false,
      agencies: [],
      teamLeads: []
    },
    commitmentsTable: {
      data: [] as ICommitment[],
      pages: {} as IPages,
      loading: false,
      reasons: {} as IReasons,
      lastUpdatedAt: 0
    },
    ptpDetailsTable: {
      data: [] as IPtpDetail[],
      pages: {} as IPages,
      filters: {} as IPtpDetailsFilters,
      loading: false,
      lastUpdatedAt: 0
    }
  },
  overallMetrics: {
    inputTable: {
      data: [],
      pages: {} as IPages,
      loading: false,
      lastUpdatedAt: 0
    },
    outputTable: {
      data: [],
      pages: {} as IPages,
      filters: {} as IPtpDetailsFilters,
      loading: false,
      lastUpdatedAt: 0
    },
    metricTableV2: {
      data: [],
      pages: {} as IPages,
      filters: {
        caseBucket: [],
        teamLeadReferenceId: [],
        showTrainingAgents: AGENT_TYPES.LIVE_AGENTS
      },
      loading: false,
      lastUpdatedAt: 0,
      metricGrouping: [],
      agentSummaryMetricKeyList: []
    },
    selectedAgentMetrics: {
      data: null,
      loading: false
    }
  }
};

const checkIfArrayOrString = (value: Array<string> | string) => {
  if (!value) return null;
  if (Array.isArray(value) && value.length) {
    return value.join(',');
  }
  return value;
};

const Sensei = createSlice({
  name: 'Sensei',
  initialState,
  reducers: {
    setCommitmentData(state, action) {
      const { commitments, reasons, lastUpdatedAt } = action.payload || {};
      const { data, pages } = commitments || {};
      const dataObject = {
        data: data?.map((item: ICommitment) => {
          const { reasonsForCashDeviation, reasonsForVisitDeviation } = item;
          return {
            ...item,
            reasonsForCashDeviation: reasonsForCashDeviation?.length
              ? reasonsForCashDeviation?.join(',')
              : null,
            reasonsForVisitDeviation: reasonsForVisitDeviation?.length
              ? reasonsForVisitDeviation?.join(',')
              : null
          };
        }),
        pages,
        reasons,
        lastUpdatedAt,
        loading: false
      };
      state.dailyPlanning.commitmentsTable = dataObject;
    },
    setCommitmentDataForARow(state, action) {
      const { data } = action.payload || {};
      if (!data) return;
      if (!state?.dailyPlanning?.commitmentsTable?.data?.length) return;
      const oldCommitmentData = [...state.dailyPlanning.commitmentsTable.data];
      const updatedData = oldCommitmentData.map((item: ICommitment) => {
        if (item.agentReferenceId === data.agentReferenceId) {
          return {
            ...data,
            reasonsForCashDeviation: checkIfArrayOrString(data?.reasonsForCashDeviation),
            reasonsForVisitDeviation: checkIfArrayOrString(data?.reasonsForVisitDeviation),
            lastUpdatedAt: Date.now()
          };
        }
        return item;
      });
      state.dailyPlanning.commitmentsTable.data = updatedData;
      state.dailyPlanning.commitmentsTable.lastUpdatedAt = Date.now();
    },
    commitmentDataLoading(state, action) {
      state.dailyPlanning.commitmentsTable.loading = action.payload;
    },
    ptpDetailsDataLoading(state, action) {
      state.dailyPlanning.ptpDetailsTable.loading = action.payload;
    },
    setPtpDetailsData(state, action) {
      const { ptpDues, filter, lastUpdatedAt } = action.payload || {};
      const { data, pages } = ptpDues || {};
      const newFilters = filter?.agents?.map((item: filtersNames) => {
        return {
          label: item.agentName,
          value: item.agentReferenceId
        };
      });
      const newPtpStatusFilter = filter?.ptpStatus?.map((item: string) => {
        return {
          label: item,
          value: item
        };
      });
      const dataObject = {
        data,
        pages,
        loading: false,
        filters: {
          agents: newFilters,
          ptpStatus: newPtpStatusFilter
        },
        lastUpdatedAt
      };
      state.dailyPlanning.ptpDetailsTable = dataObject;
    },
    setDailyPlanningFiltersLoading: (state, action) => {
      state.dailyPlanning.filters.loading = action.payload;
    },
    setDailyPlanningFiltersData(state, action) {
      const { agencies = [], users = [] } = action.payload || {};
      const agenciesList = agencies?.map(item => {
        return {
          label: item.agencyName,
          value: item.agencyCode
        };
      });
      const teamLeadsList = users?.map(item => {
        return {
          label: item.name,
          value: item.referenceId,
          additionalData: {
            agencyCode: item.agencyCode
          }
        };
      });
      state.dailyPlanning.filters = {
        loading: false,
        agencies: agenciesList,
        teamLeads: teamLeadsList
      };
    },
    setSelectedAgentOverallMetricLoading: (state, action) => {
      state.overallMetrics.selectedAgentMetrics.loading = action.payload;
    },
    setSelectedAgentOverallMetric: (state, action) => {
      state.overallMetrics.selectedAgentMetrics.data = action.payload;
    },
    setOutputTableData(state, action) {
      const { performanceData, filter } = action.payload || {};
      const { data, pages } = performanceData || {};
      const newData = {
        data,
        pages,
        loading: false,
        lastUpdatedAt: Date.now(),
        filters: filter
      };
      state.overallMetrics.outputTable = newData as IOutputTable;
    },
    setMetricTableV2Loading(state, action) {
      state.overallMetrics.metricTableV2.loading = action.payload;
    },
    setMetricTableV2Data(state, action) {
      const { performanceData, filter, metricGrouping, agentSummaryMetricKeyList, lastUpdatedAt } =
        action.payload || {};
      const { data, pages } = performanceData || {};
      const newData = {
        data,
        pages,
        loading: false,
        lastUpdatedAt,
        filters: filter,
        metricGrouping,
        agentSummaryMetricKeyList
      };
      state.overallMetrics.metricTableV2 = newData;
    },
    setOutputTableLoading(state, action) {
      state.overallMetrics.outputTable.loading = action.payload;
    },
    setInputTableLoading(state, action) {
      state.overallMetrics.inputTable.loading = action.payload;
    },
    setInputTableData(state, action) {
      const { performanceData, filter } = action.payload || {};
      const { data, pages } = performanceData || {};
      const newData = {
        data,
        pages,
        loading: false,
        lastUpdatedAt: Date.now(),
        filters: filter
      } as IInputTable;
      state.overallMetrics.inputTable = newData;
    }
  }
});

export const {
  setCommitmentData,
  commitmentDataLoading,
  ptpDetailsDataLoading,
  setPtpDetailsData,
  setCommitmentDataForARow,
  setOutputTableData,
  setOutputTableLoading,
  setInputTableLoading,
  setInputTableData,
  setDailyPlanningFiltersLoading,
  setDailyPlanningFiltersData,
  setMetricTableV2Loading,
  setMetricTableV2Data,
  setSelectedAgentOverallMetric,
  setSelectedAgentOverallMetricLoading
} = Sensei.actions;

export default Sensei.reducer;
